// src/pages/ForgotPassword.jsx
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { authenticatedFetch } from '../utils/api';

// ✅ Match Login.jsx API_BASE (no trailing spaces)
const API_BASE = (process.env.REACT_APP_API_URL );

function ForgotPassword() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '' });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear field error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'Email address is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    setErrors({});
    
    try {
      // ✅ Payload: Email only for forgot password
      const payload = {
        Email: formData.email.trim().toLowerCase()
      };

      console.log('📤 Forgot Password Request:', payload);

      // ✅ Call backend forgot password endpoint
      const response = await authenticatedFetch(
        `${API_BASE}/HMO/forgot-password`,  // ✅ Update endpoint name to match your backend
        'POST',
        payload
      );

      console.log('✅ Forgot Password Response:', response);

      // ✅ Handle success
      if (response?.success === true || response?.status === 'success') {
        setIsSubmitted(true);
        toast.success(response.message || 'Password reset instructions sent! Check your email.');
        
        // Optional: Auto-redirect to login after delay
        setTimeout(() => {
          navigate('/login', { 
            replace: true,
            state: { message: 'Check your email for password reset instructions.' }
          });
        }, 5000);
        
      } else {
        // ✅ Handle business logic errors
        const errorMsg = response?.message || 'Failed to process request. Please try again.';
        
        if (errorMsg.toLowerCase().includes('not found') || errorMsg.toLowerCase().includes('email')) {
          setErrors({ submit: 'No account found with that email address.' });
          toast.warning('Email not registered');
        } else {
          setErrors({ submit: errorMsg });
          toast.error(errorMsg);
        }
      }
      
    } catch (error) {
      console.error('💥 Forgot Password Error:', { message: error.message, name: error.name });
      
      let msg = 'An error occurred. Please try again.';
      
      if (error.message?.includes('Missing headers') || 
          error.message?.includes('AuthorizationKey') ||
          error.message?.includes('Token') ||
          error.message?.includes('SecretKey')) {
        msg = 'Authentication configuration error. Please contact support.';
      } else if (error.message?.includes('Failed to fetch') || error.name === 'TypeError') {
        msg = 'Cannot connect to server. Check your internet connection.';
      } else if (error.message?.includes('404') || error.message?.includes('Not Found')) {
        msg = 'Email not registered. Please check or sign up for a new account.';
      }
      
      setErrors({ submit: msg });
      toast.error(msg);
      
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ Success State: Show confirmation message
  if (isSubmitted) {
    return (
      <div className="forgot-password-page">
        <ToastContainer position="top-right" autoClose={4000} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="light" />

        {/* Header */}
        <header className="header-area header-one">
          <div className="container">
            <div className="d-flex justify-content-between align-items-center py-3">
              <Link to="/" className="brand-logo" aria-label="FSL HMO Home">
                <img src="images/fsl_logo.png" alt="FSL HMO Logo" style={{ height: '50px' }} />
              </Link>
              <button className="theme-btn style-two" onClick={() => navigate('/')} aria-label="Back to Home">← Back to Home</button>
            </div>
          </div>
        </header>

        {/* Success Section */}
        <section className="forgot-password-section py-5">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-5 col-md-7">
                <div className="login-container">
                  <div className="text-center mb-4">
                    <div className="success-icon mb-3">
                      <i className="fas fa-check-circle text-success" style={{ fontSize: '48px' }}></i>
                    </div>
                    <h1 className="login-title mb-2">Check Your Email</h1>
                    <p className="login-subtitle">
                      We've sent password reset instructions to <strong>{formData.email}</strong>
                    </p>
                  </div>

                  <div className="alert alert-info mb-4" role="alert">
                    <i className="fas fa-info-circle me-2"></i>
                    <small>Didn't receive the email? Check your spam folder or <button className="btn btn-link p-0" onClick={() => setIsSubmitted(false)}>try again</button>.</small>
                  </div>

                  <div className="d-grid gap-2">
                    <button 
                      className="btn btn-login"
                      onClick={() => navigate('/login')}
                    >
                      Return to Login <i className="fas fa-arrow-right ms-2"></i>
                    </button>
                    <Link to="/get-started" className="btn btn-outline-secondary">
                      Create New Account
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CSS - Reuse Login styles + add success icon */}
        <style>{`
          .forgot-password-page { min-height: 100vh; background-color: #fafafa; display: flex; flex-direction: column; font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
          .forgot-password-section { flex: 1; display: flex; align-items: center; }
          .login-container { background: white; border-radius: 16px; box-shadow: 0 12px 40px rgba(0,0,0,0.08); padding: 40px; width: 100%; animation: slideUp 0.5s ease; border-top: 4px solid #F89423; }
          @keyframes slideUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
          .login-title { font-size: 28px; font-weight: 700; color: #1f2937; margin-bottom: 8px; }
          .login-subtitle { color: #6b7280; font-size: 14px; margin: 0; }
          .success-icon { animation: pulse 2s infinite; }
          @keyframes pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.05); } }
          .alert { padding: 12px 16px; border-radius: 10px; font-size: 14px; display: flex; align-items: flex-start; }
          .alert-info { background-color: #eff6ff; color: #1e40af; border: 1px solid #bfdbfe; }
          .alert-info i { margin-top: 2px; }
          .btn-login { background: linear-gradient(135deg, #F89423 0%, #ea7c1f 100%); border: none; color: white; padding: 14px 20px; font-size: 15px; font-weight: 600; border-radius: 10px; cursor: pointer; transition: all 0.3s ease; display: flex; align-items: center; justify-content: center; width: 100%; }
          .btn-login:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(248,148,35,0.4); }
          .btn-outline-secondary { background: white; border: 1px solid #d1d5db; color: #4b5563; padding: 14px 20px; font-size: 15px; font-weight: 600; border-radius: 10px; cursor: pointer; transition: all 0.3s ease; display: flex; align-items: center; justify-content: center; width: 100%; text-decoration: none; }
          .btn-outline-secondary:hover { background: #f9fafb; border-color: #9ca3af; }
          @media (max-width: 768px) { .login-container { padding: 30px 20px; border-radius: 12px; } .login-title { font-size: 24px; } }
          @media (max-width: 480px) { .forgot-password-section { padding: 20px 0; } .login-container { padding: 25px 15px; } }
        `}</style>
      </div>
    );
  }

  // ✅ Form State: Show forgot password form
  return (
    <div className="forgot-password-page">
      <ToastContainer position="top-right" autoClose={4000} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="light" />

      {/* Header */}
      <header className="header-area header-one">
        <div className="container">
          <div className="d-flex justify-content-between align-items-center py-3">
            <Link to="/" className="brand-logo" aria-label="FSL HMO Home">
              <img src="images/fsl_logo.png" alt="FSL HMO Logo" style={{ height: '50px' }} />
            </Link>
            <button className="theme-btn style-two" onClick={() => navigate('/')} disabled={isLoading} aria-label="Back to Home">← Back to Home</button>
          </div>
        </div>
      </header>

      {/* Forgot Password Form */}
      <section className="forgot-password-section py-5">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-5 col-md-7">
              <div className="login-container">
                <div className="login-header text-center mb-4">
                  <h1 className="login-title mb-2">Forgot Password?</h1>
                  <p className="login-subtitle">Enter your email to receive reset instructions</p>
                </div>

                <form onSubmit={handleSubmit} className="login-form" noValidate>
                  {/* Global Error Message */}
                  {errors.submit && (
                    <div className="alert alert-danger mb-3" role="alert" aria-live="polite">
                      <i className="fas fa-exclamation-circle me-2" aria-hidden="true"></i>
                      {errors.submit}
                    </div>
                  )}

                  {/* Email Field */}
                  <div className="form-group mb-4">
                    <label htmlFor="email" className="form-label">
                      Email address <span className="text-danger">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                      placeholder="Enter your registered email"
                      value={formData.email}
                      onChange={handleChange}
                      disabled={isLoading}
                      autoComplete="email"
                      required
                      aria-invalid={!!errors.email}
                      aria-describedby={errors.email ? 'email-error' : undefined}
                    />
                    {errors.email && (
                      <div id="email-error" className="invalid-feedback" role="alert">
                        {errors.email}
                      </div>
                    )}
                    <small className="text-muted d-block mt-2">
                      <i className="fas fa-lock me-1"></i>
                      We'll send a secure link to reset your password
                    </small>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    className="btn btn-login w-100 mb-3"
                    disabled={isLoading}
                    aria-busy={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Reset Link <i className="fas fa-paper-plane ms-2"></i>
                      </>
                    )}
                  </button>

                  {/* Back to Login */}
                  <div className="text-center">
                    <p className="mb-0 text-muted">
                      Remember your password?{' '}
                      <Link to="/login" className="signup-link">
                        Back to Login
                      </Link>
                    </p>
                  </div>

                  {/* Security Notice */}
                  <div className="text-center mt-4 pt-3 border-top">
                    <small className="text-muted">
                      <i className="fas fa-shield-alt me-1" aria-hidden="true"></i>
                      Reset links expire in 1 hour for your security
                    </small>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CSS - Match Login.jsx styling */}
      <style>{`
        .forgot-password-page { min-height: 100vh; background-color: #fafafa; display: flex; flex-direction: column; font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
        .forgot-password-section { flex: 1; display: flex; align-items: center; }
        .login-container { background: white; border-radius: 16px; box-shadow: 0 12px 40px rgba(0,0,0,0.08); padding: 40px; width: 100%; animation: slideUp 0.5s ease; border-top: 4px solid #F89423; }
        @keyframes slideUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        .login-header { margin-bottom: 30px; }
        .login-title { font-size: 28px; font-weight: 700; color: #1f2937; margin-bottom: 8px; }
        .login-subtitle { color: #6b7280; font-size: 14px; margin: 0; }
        .form-label { font-weight: 600; color: #374151; font-size: 14px; margin-bottom: 8px; display: block; }
        .form-control { padding: 12px 16px; border: 1px solid #d1d5db; border-radius: 10px; font-size: 14px; transition: all 0.3s ease; background-color: #f9fafb; width: 100%; }
        .form-control:focus { border-color: #F89423; box-shadow: 0 0 0 4px rgba(248,148,35,0.15); background-color: white; outline: none; }
        .form-control.is-invalid { border-color: #ef4444; background-color: #fef2f2; }
        .form-control.is-invalid:focus { box-shadow: 0 0 0 4px rgba(239,68,68,0.15); }
        .form-control:disabled { background-color: #f3f4f6; cursor: not-allowed; opacity: 0.8; }
        .invalid-feedback { color: #ef4444; font-size: 12px; margin-top: 4px; display: block; }
        .alert { padding: 12px 16px; border-radius: 10px; font-size: 14px; display: flex; align-items: flex-start; }
        .alert-danger { background-color: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }
        .alert-danger i { margin-top: 2px; }
        .alert-info { background-color: #eff6ff; color: #1e40af; border: 1px solid #bfdbfe; }
        .alert-info i { margin-top: 2px; }
        .btn-login { background: linear-gradient(135deg, #F89423 0%, #ea7c1f 100%); border: none; color: white; padding: 14px 20px; font-size: 15px; font-weight: 600; border-radius: 10px; cursor: pointer; transition: all 0.3s ease; display: flex; align-items: center; justify-content: center; width: 100%; }
        .btn-login:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(248,148,35,0.4); }
        .btn-login:disabled { opacity: 0.75; cursor: not-allowed; transform: none; }
        .signup-link { color: #F89423; text-decoration: none; font-weight: 600; }
        .signup-link:hover { color: #ea7c1f; text-decoration: underline; }
        .spinner-border { width: 1rem; height: 1rem; border-width: 0.15em; vertical-align: text-bottom; }
        @media (max-width: 768px) { .login-container { padding: 30px 20px; border-radius: 12px; } .login-title { font-size: 24px; } }
        @media (max-width: 480px) { .forgot-password-section { padding: 20px 0; } .login-container { padding: 25px 15px; } .btn-login { padding: 12px; font-size: 14px; } }
        .form-control:focus-visible, .btn-login:focus-visible { outline: 2px solid #F89423; outline-offset: 2px; }
      `}</style>
    </div>
  );
}

export default ForgotPassword;